import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { servicesData, serviceCategories } from '../../mocks/services';

const ServicesPage = () => {
  const [showAdBanner, setShowAdBanner] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const navigate = useNavigate();

  const filteredServices = selectedCategory === 'all' 
    ? servicesData 
    : servicesData.filter(service => service.category === selectedCategory);

  const handleLearnMore = (serviceId: string) => {
    navigate(`/service-detail?id=${serviceId}`);
  };

  const handleGetStarted = (planName: string) => {
    navigate('/checkout');
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Floating Advertisement Banner */}
      {showAdBanner && (
        <div className="fixed bottom-6 right-6 z-50 max-w-sm animate-slide-up">
          <div className="bg-gradient-to-br from-primary via-purple-600 to-accent-green rounded-2xl shadow-2xl overflow-hidden">
            <button
              onClick={() => setShowAdBanner(false)}
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full text-white transition-colors cursor-pointer z-10"
            >
              <i className="ri-close-line"></i>
            </button>
            
            <div className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                <span className="text-white text-xs font-bold uppercase tracking-wider">Hot Deal</span>
              </div>
              
              <h3 className="text-white font-bold text-xl mb-2">
                🔥 Limited Time Offers!
              </h3>
              
              <div className="space-y-3 mb-4">
                <div 
                  onClick={() => {
                    navigate('/products');
                    setShowAdBanner(false);
                  }}
                  className="bg-white/10 backdrop-blur-sm rounded-lg p-3 hover:bg-white/20 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                      <i className="ri-macbook-line text-2xl text-white"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold text-sm">MacBook Pro 16"</p>
                      <p className="text-white/80 text-xs">Save $500 Today!</p>
                    </div>
                  </div>
                </div>
                
                <div 
                  onClick={() => {
                    navigate('/products');
                    setShowAdBanner(false);
                  }}
                  className="bg-white/10 backdrop-blur-sm rounded-lg p-3 hover:bg-white/20 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                      <i className="ri-plane-line text-2xl text-white"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold text-sm">Maldives Luxury</p>
                      <p className="text-white/80 text-xs">Earn $1,500 Commission!</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => {
                  navigate('/products');
                  setShowAdBanner(false);
                }}
                className="w-full py-3 bg-white text-primary rounded-lg font-bold hover:shadow-xl transition-all cursor-pointer whitespace-nowrap"
              >
                Shop Now <i className="ri-arrow-right-line ml-1"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="relative w-full h-96 rounded-3xl overflow-hidden mb-12">
            <img
              src="https://public.readdy.ai/ai/img_res/b51735234b91a416782d40c491a55326.jpg"
              alt="Services Hero"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent flex items-center">
              <div className="px-12">
                <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-semibold mb-4">
                  100% AUTOMATED FULFILLMENT
                </div>
                <h1 className="font-serif text-5xl md:text-6xl font-bold text-white mb-4">
                  Professional Digital Services
                </h1>
                <p className="text-xl text-white/90 max-w-2xl">
                  <strong>Fully automated delivery</strong> - Services fulfilled directly by expert partners. You never handle projects. Just earn 30% commission!
                </p>
              </div>
            </div>
          </div>

          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Zero Work, Pure Profit<br />
              <span className="text-gray-500 font-light italic">Services Delivered by Experts</span>
            </h2>
          </div>
        </div>
      </section>

      {/* Automation Benefits Banner */}
      <section className="py-12 px-6 lg:px-12 bg-gradient-to-r from-accent-green/10 to-primary/10">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="w-16 h-16 flex items-center justify-center bg-accent-green/10 rounded-full mx-auto mb-3">
                <i className="ri-user-star-line text-3xl text-accent-green"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Expert Partners</h3>
              <p className="text-gray-600 text-sm">Professional teams handle all work</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="w-16 h-16 flex items-center justify-center bg-accent-green/10 rounded-full mx-auto mb-3">
                <i className="ri-time-line text-3xl text-accent-green"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">No Time Required</h3>
              <p className="text-gray-600 text-sm">Zero hours of work from you</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="w-16 h-16 flex items-center justify-center bg-accent-green/10 rounded-full mx-auto mb-3">
                <i className="ri-shield-check-line text-3xl text-accent-green"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Quality Guaranteed</h3>
              <p className="text-gray-600 text-sm">Professional results every time</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="w-16 h-16 flex items-center justify-center bg-accent-green/10 rounded-full mx-auto mb-3">
                <i className="ri-money-dollar-circle-line text-3xl text-accent-green"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">30% Commission</h3>
              <p className="text-gray-600 text-sm">Earn on every service sold</p>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-12 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-3 justify-center mb-12">
            {serviceCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 ${
                  selectedCategory === category.id
                    ? 'bg-gradient-to-r from-primary to-purple-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className={`${category.icon} text-lg`}></i>
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="pb-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="space-y-16">
            {filteredServices.map((service, index) => (
              <div
                key={service.id}
                className={`flex flex-col ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                } gap-12 items-center`}
              >
                <div className="flex-1 w-full h-96 relative">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover object-top rounded-2xl shadow-lg"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-primary text-white text-xs font-bold rounded-full">
                      Auto-Fulfilled
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-accent-green text-white text-xs font-bold rounded-full">
                      {service.category}
                    </span>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="font-serif text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    {service.title}
                  </h3>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <div className="mb-6">
                    <div className="flex items-baseline gap-4 mb-2">
                      <p className="text-3xl font-bold text-primary">
                        From ${service.price}
                      </p>
                      <div className="flex items-center gap-2 bg-accent-green/10 px-4 py-2 rounded-full">
                        <i className="ri-hand-coin-line text-accent-green"></i>
                        <span className="text-sm font-bold text-accent-green">
                          You earn: ${service.commission}
                        </span>
                      </div>
                    </div>
                    <ul className="space-y-3 mt-6">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-3">
                          <div className="w-6 h-6 flex items-center justify-center bg-accent-green/10 text-accent-green rounded-full flex-shrink-0">
                            <i className="ri-check-line text-sm"></i>
                          </div>
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <button 
                    onClick={() => handleLearnMore(service.id)}
                    className="px-8 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Learn More <i className="ri-arrow-right-line ml-2"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How Payment Works */}
      <section className="py-24 px-6 lg:px-12 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              How Automated Services Work
            </h2>
            <p className="text-lg text-gray-600">
              Completely hands-free - you never do the work
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-16">
            {[
              {
                icon: 'ri-shopping-cart-line',
                title: 'Customer Purchase',
                desc: 'Client buys service through your platform',
              },
              {
                icon: 'ri-arrow-right-line',
                title: 'Auto-Assign',
                desc: 'Project automatically sent to expert partner',
              },
              {
                icon: 'ri-tools-line',
                title: 'Partner Delivers',
                desc: 'Professional team completes the work',
              },
              {
                icon: 'ri-money-dollar-circle-line',
                title: 'You Get Paid',
                desc: 'Receive 30% commission instantly',
              },
            ].map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-xl p-8 text-center shadow-md hover:shadow-xl transition-shadow">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-primary to-purple-600 text-white rounded-full mx-auto mb-4">
                    <i className={`${step.icon} text-2xl`}></i>
                  </div>
                  <div className="absolute -top-3 -right-3 w-8 h-8 bg-accent-green text-white rounded-full flex items-center justify-center font-bold text-sm">
                    {index + 1}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-600">{step.desc}</p>
                </div>
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <i className="ri-arrow-right-line text-2xl text-gray-300"></i>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-accent-green/10 text-accent-green rounded-full mx-auto mb-4">
                  <i className="ri-hand-coin-line text-2xl"></i>
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">Zero Work Required</h3>
                <p className="text-gray-600">You never touch the projects - partners handle everything</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-accent-green/10 text-accent-green rounded-full mx-auto mb-4">
                  <i className="ri-flashlight-line text-2xl"></i>
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">Instant Payouts</h3>
                <p className="text-gray-600">Get paid immediately after project approval</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-accent-green/10 text-accent-green rounded-full mx-auto mb-4">
                  <i className="ri-line-chart-line text-2xl"></i>
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">Passive Income</h3>
                <p className="text-gray-600">Earn commissions while you sleep</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Comparison */}
      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Choose Your Plan
            </h2>
            <p className="text-lg text-gray-600">
              All plans include automated fulfillment - zero work from you
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: 'STARTER',
                price: '$499',
                commission: '$149',
                features: ['1 Service', 'Email Support', '30-Day Delivery', 'Basic Features'],
                popular: false,
              },
              {
                name: 'PROFESSIONAL',
                price: '$999',
                commission: '$299',
                features: ['3 Services', 'Priority Support', '14-Day Delivery', 'Advanced Features', 'Free Revisions'],
                popular: true,
              },
              {
                name: 'ENTERPRISE',
                price: '$1,999',
                commission: '$599',
                features: ['Unlimited Services', '24/7 Support', '7-Day Delivery', 'All Features', 'Dedicated Manager', 'Custom Solutions'],
                popular: false,
              },
            ].map((plan, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl p-8 ${
                  plan.popular
                    ? 'ring-2 ring-primary shadow-2xl transform lg:-translate-y-4'
                    : 'shadow-md'
                }`}
              >
                {plan.popular && (
                  <div className="inline-block px-4 py-1 bg-primary text-white text-xs font-bold rounded-full mb-4">
                    MOST POPULAR
                  </div>
                )}
                <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4">
                  {plan.name}
                </h3>
                <div className="mb-2">
                  <span className="text-5xl font-bold text-gray-900">{plan.price}</span>
                  <span className="text-gray-500">/month</span>
                </div>
                <div className="mb-6 flex items-center gap-2 bg-accent-green/10 px-3 py-2 rounded-lg inline-flex">
                  <i className="ri-hand-coin-line text-accent-green"></i>
                  <span className="text-sm font-bold text-accent-green">
                    You earn: {plan.commission}
                  </span>
                </div>
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <i className="ri-check-line text-accent-green text-xl"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleGetStarted(plan.name)}
                  className={`w-full py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    plan.popular
                      ? 'bg-gradient-to-r from-primary to-purple-600 text-white hover:shadow-lg'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
